/**
 * Source registry + federation engine — per E-023 §A and §C.
 *
 * Sources register at app-startup time. The federation engine filters by
 * visibility tier (E-020) BEFORE permission checks BEFORE the query runs.
 *
 * Registration is idempotent — re-registering the same `id` overwrites
 * (HMR-friendly during dev). Warn but don't throw.
 *
 * The engine NEVER panics on a single source's failure — a slow or broken
 * source has its result excluded from this query but does not poison the
 * federation. Failures are logged via the `onError` hook (defaults to
 * console.warn).
 */
import { compile } from "../parser/compiler.js";
import { parse } from "../parser/grammar.js";
import { score } from "../ranker/score.js";
import { canSourceMountOn } from "../visibility/index.js";
// ─── Registry ─────────────────────────────────────────────────────────────────
const sources = new Map();
/**
 * Register (or replace) a search source by id. Returns the previous source if
 * one was overwritten, undefined otherwise.
 */
export function registerSource(s) {
    const prior = sources.get(s.id);
    if (prior && (typeof process === "undefined" || process?.env?.NODE_ENV !== "test")) {
        // eslint-disable-next-line no-console
        console.warn(`[search-bar] re-registering source "${s.id}" (HMR?)`);
    }
    sources.set(s.id, s);
    return prior;
}
export function unregisterSource(id) {
    return sources.delete(id);
}
export function getSources() {
    return [...sources.values()];
}
export function getSource(id) {
    return sources.get(id);
}
/** Test-only: clear the registry. Avoid in app code. */
export function _clearRegistry() {
    sources.clear();
}
/**
 * Run a federated search across all registered sources allowed by the mount
 * tier and the user's permissions. Returns up to `limit` ranked hits sorted
 * by score descending.
 *
 * The function is the SECURITY GATE for E-020 + F-023:
 *
 *   1. Visibility tier filter — drops every source above the mount.
 *   2. Permission check — calls source.permissions(ctx); drop on false.
 *   3. RLS double-check — for hits with hit.raw.bookId, assert it equals
 *      ctx.bookId when both are defined. This is defense-in-depth ON TOP
 *      OF the F-023 $allOperations Prisma extension; it would never fire
 *      under correct middleware but logs (operator-only — Phase 5 wire-up)
 *      when it does, surfacing any future regression.
 */
export async function search(raw, ctx, opts = {}) {
    const parsed = parse(raw);
    const plan = compile(parsed);
    const mount = opts.mount ?? "PRIVATE";
    const limit = opts.limit ?? 20;
    const now = opts.now ?? new Date();
    const onError = opts.onError ?? defaultOnError;
    const timeoutMs = opts.sourceTimeoutMs ?? 5000;
    // Visibility filter (E-020) — synchronous, hard wall.
    const visibilityAllowed = getSources().filter((s) => canSourceMountOn(s, mount));
    // Permission filter (F-023 + custom rules) — async, per-source.
    const permitted = [];
    await Promise.all(visibilityAllowed.map(async (s) => {
        try {
            const ok = await s.permissions(ctx);
            if (ok)
                permitted.push(s);
        }
        catch (err) {
            onError(s.id, err);
        }
    }));
    // Run all permitted sources in parallel with timeout protection.
    const sourceResults = await Promise.all(permitted.map(async (s) => {
        try {
            const hits = await withTimeout(s.query({ parsed, plan, ctx }), timeoutMs, `source ${s.id} timed out after ${timeoutMs}ms`);
            return hits.map((h) => score(h, plan, s, now));
        }
        catch (err) {
            onError(s.id, err);
            return [];
        }
    }));
    // Flatten, drop hard exclusions, RLS double-check, sort by score, take top-K.
    return sourceResults
        .flat()
        .filter((h) => h.score > -Infinity)
        .filter((h) => rlsDoubleCheck(h, ctx))
        .sort((a, b) => b.score - a.score)
        .slice(0, limit);
}
function rlsDoubleCheck(hit, ctx) {
    if (!ctx.bookId)
        return true;
    const raw = hit.raw;
    if (!raw || raw.bookId === undefined)
        return true; // operator-global record (e.g., Contact)
    return raw.bookId === ctx.bookId;
}
function withTimeout(p, ms, msg) {
    return new Promise((resolve, reject) => {
        const t = setTimeout(() => reject(new Error(msg)), ms);
        p.then((v) => {
            clearTimeout(t);
            resolve(v);
        }, (e) => {
            clearTimeout(t);
            reject(e);
        });
    });
}
function defaultOnError(sourceId, err) {
    // eslint-disable-next-line no-console
    console.warn(`[search-bar] source "${sourceId}" failed:`, err);
}
//# sourceMappingURL=index.js.map